import { PersonDto } from "api/contract";
import { Sizes } from "components/forms/NumberSelector";
import { NsKey } from "enums/NsKey";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { usePanel } from "hooks/usePanel";
import { Building2 } from "lucide-react";
import { FC, useCallback } from "react";
import { SubscriptionProcessContext, useSubscriptionProcess } from "features/subscription-process/contexts/SubscriptionProcessContext";
import { LegalProtectionCompanyCard } from "./LegalProtectionCompanyCard";
import { LegalProtectionSection } from "./LegalProtectionSection";
import { LegalEntityFinancialsFormValues } from "features/new-actor/types/optim/legalEntityFinancialsFormValues";
import { withPersonName } from "shared/utils/panelTitle";
import { isLegalEntity } from "shared/utils/person";
import { DashedAddButton } from "components/ui/DashedAddButton";
import { UpsertLegalProtectionCompany } from "./UpsertLegalProtectionCompany";

interface LegalProtectionCompanySectionProps {
  /** Company of the folder. Undefined: none registered yet. */
  company?: PersonDto;
  /** Folder the company is registered on. */
  riskId: string;
  /** Accounting data of the company as saved, shown on its card. */
  financials?: LegalEntityFinancialsFormValues | null;
  /** Reloads the step data once the company is saved. */
  onSaved?: () => void | Promise<void>;
}

/**
 * Company of a legal protection folder: a white card once chosen, an empty one before.
 * Searching an existing company is inside the panel, not on the page.
 */
export const LegalProtectionCompanySection: FC<LegalProtectionCompanySectionProps> = ({ company, riskId, financials, onSaved }) => {
  const { t } = useDynamicTranslation([NsKey.Common]);
  const { openPanel } = usePanel();
  // Panels are rendered outside the route tree: hand them the folder context, as InsuredUnemploymentCard does.
  const subscriptionProcess = useSubscriptionProcess();

  // Creating and editing open the same panel: the one in creation carries the search.
  const openCompanyPanel = useCallback((selected?: PersonDto) => {
    openPanel(
      {
        title: selected
          // Same title as the borrower panels: the action, then whose record it is.
          ? withPersonName(
            t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.EDIT"),
            isLegalEntity(selected) ? selected.companyName : undefined
          )
          : t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.ADD_COMPANY"),
        size: Sizes["2xl"],
      },
      <SubscriptionProcessContext.Provider value={subscriptionProcess}>
        <UpsertLegalProtectionCompany company={selected} riskId={riskId} onSaved={onSaved} />
      </SubscriptionProcessContext.Provider>
    );
  }, [openPanel, t, subscriptionProcess, riskId, onSaved]);

  return (
    <LegalProtectionSection
      icon={<Building2 className="h-8 w-8 text-primary" />}
      title={t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.COMPANY")}
    >
      {company
        ? <LegalProtectionCompanyCard company={company} financials={financials} onEdit={() => openCompanyPanel(company)} />
        : (
          <DashedAddButton
            label={t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.ADD_COMPANY")}
            onClick={() => openCompanyPanel()}
          />
        )}
    </LegalProtectionSection>
  );
};
